import React from "react"
import {Box, Card, Avatar, Typography, Alert, Button, IconButton} from "@mui/material"
import {useFormContext, useWatch} from "react-hook-form"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import AddIcon from "@mui/icons-material/Add"
import CloseIcon from "@mui/icons-material/Close"

import {OfferFormDataV2, MAX_PAYMENT_PLAN_TIERS} from "./offerFormValidation"
import {PaymentPlanTierCard} from "./PaymentPlanTierCard"
import {PaymentPlanIcon} from "./PaymentPlanIcon"

import {colors} from "@indebted/theme/colors"

export function PaymentPlanSettlementCard({
	onDeleteAll,
	balanceInCents,
}: {
	onDeleteAll: () => void
	balanceInCents: number
}) {
	const {
		control,
		setValue,
		formState: {errors},
	} = useFormContext<OfferFormDataV2>()
	const watchPaymentPlanTiers = useWatch({control, name: "paymentPlan.tiers"}) ?? []

	const hasErrors = Boolean(
		errors.paymentPlan?.tiers &&
		Array.isArray(errors.paymentPlan.tiers) &&
		errors.paymentPlan.tiers.some(
			(tier, index) =>
				(tier?.discountRate || tier?.maxTimeToPayWeeks) && !watchPaymentPlanTiers[index]?.isEditing,
		),
	)

	const handleDeleteAll = () => {
		setValue("paymentPlan.tiers", [])
		onDeleteAll()
	}

	const handleDeleteTier = (tierId: string) => {
		const newTiers = watchPaymentPlanTiers.filter((tier) => tier.id !== tierId)
		if (newTiers.length === 0) {
			setValue("paymentPlan.tiers", [])
			onDeleteAll()
		} else {
			setValue("paymentPlan.tiers", newTiers)
		}
	}

	const handleAddTier = () => {
		if (watchPaymentPlanTiers.length < MAX_PAYMENT_PLAN_TIERS) {
			const newTier = {id: crypto.randomUUID(), discountRate: 0, maxTimeToPayWeeks: 0, isEditing: true}
			setValue("paymentPlan.tiers", [...watchPaymentPlanTiers, newTier])
		}
	}

	return (
		<Box>
			<Card
				sx={{
					backgroundColor: colors.warmBlue["50"],
					border: `1px solid ${colors.warmBlue["400"]}`,
					borderBottom: "none",
					borderRadius: "0.25rem 0.25rem 0 0",
				}}
				elevation={0}
			>
				<Box sx={{display: "flex", alignItems: "center", p: "1rem", gap: "1rem"}}>
					<Avatar sx={{backgroundColor: colors.warmBlue["900"], width: "2rem", height: "2rem"}}>
						<PaymentPlanIcon sx={{fontSize: "1.25rem", color: colors.primary.white}} />
					</Avatar>
					<Box sx={{flex: 1}}>
						<Typography sx={{fontSize: "0.875rem", fontWeight: 500, color: "rgba(0,0,0,0.87)"}}>
							Payment plan settlement
						</Typography>
						<Typography sx={{fontSize: "0.75rem", color: "rgba(0,0,0,0.6)"}}>
							Customer settles the balance over multiple payments.
						</Typography>
					</Box>
					<IconButton size="small" onClick={handleDeleteAll} sx={{color: "rgba(0,0,0,0.54)"}}>
						<CloseIcon />
					</IconButton>
				</Box>
			</Card>
			<Box
				sx={{
					backgroundColor: colors.warmBlue["25"],
					border: `1px solid ${colors.warmBlue["400"]}`,
					borderTop: "none",
					borderRadius: "0 0 0.25rem 0.25rem",
					p: "1rem",
					display: "flex",
					flexDirection: "column",
					gap: "1rem",
				}}
			>
				<Typography sx={{fontSize: "0.875rem", fontWeight: 500, color: "rgba(0,0,0,0.87)"}}>
					Payment plan settlement options
				</Typography>

				{watchPaymentPlanTiers.map((tier, index) => (
					<PaymentPlanTierCard
						key={tier.id}
						tierId={tier.id}
						index={index}
						onDelete={() => handleDeleteTier(tier.id)}
						balanceInCents={balanceInCents}
					/>
				))}

				{watchPaymentPlanTiers.length < MAX_PAYMENT_PLAN_TIERS && (
					<Box display="flex" justifyContent="flex-start">
						<Button
							variant="contained"
							size="small"
							startIcon={<AddIcon />}
							onClick={handleAddTier}
							sx={{
								textTransform: "uppercase",
								backgroundColor: colors.warmBlue["900"],
								"&:hover": {backgroundColor: colors.warmBlue["800"]},
							}}
						>
							Add option
						</Button>
					</Box>
				)}

				{hasErrors && (
					<Alert severity="error" icon={<ErrorOutlineIcon />}>
						<Typography variant="body1" fontWeight="medium">
							Configuration errors detected
						</Typography>
						<Typography variant="body2">
							Please fix the errors highlighted above before creating the settlement offer.
						</Typography>
					</Alert>
				)}
			</Box>
		</Box>
	)
}
